import React from 'react';
import theme from './theme';
import './PostedLedgerEntries.css';

export default function PostedLedgerEntries({ entries, entities = [], deals = [], currency, formatAmount, accountHint }) {
  const groups = [];
  entries.forEach(entry => {
    const books = entities.find(entity => String(entity.id) === String(entry.entity))?.short_name || entry.entity_name || 'Unassigned entity';
    const ccy = entry.currency || currency || '';
    const key = JSON.stringify([entry.entity, books, ccy]);
    let group = groups.find(item => item.key === key);
    if (!group) { group = { key, books, currency: ccy, entries: [] }; groups.push(group); }
    group.entries.push(entry);
  });
  const intercompany = entries.some(entry => entry.account?.startsWith('Assets:Intercompany:'));
  const explanation = (entry, books) => {
    const debit = entry.side === 'Debit';
    const account = entry.account || '';
    if (account.startsWith('Assets:Intercompany:')) {
      const other = account.slice('Assets:Intercompany:'.length);
      return debit ? `Intercompany — ${other} owes ${books} for this entry.` : `Intercompany — ${books} owes ${other} for this entry.`;
    }
    if (account.startsWith('Assets:Cash')) return debit ? 'Money received into this account.' : 'Money paid from this account.';
    if (/^Expenses?:/.test(account)) return debit ? `Charge recorded on ${books}'s books.` : `Charge reduced on ${books}'s books.`;
    return '';
  };
  return <section className="posted-ledger" style={{
    '--ledger-border': theme.borderLight, '--ledger-header': theme.tableHeaderBg,
    '--ledger-muted': theme.textSecondary, '--ledger-mono': theme.fontMono,
    color: theme.text, background: theme.bgInput, borderColor: theme.border,
  }} aria-label="Posted ledger entries">
    <div className="ledger-heading"><h3>Posted Ledger Entries <span>({entries.length})</span></h3>
      {intercompany && <p>Each entity has its own books. When one entity pays or receives money for another, the entries below record who owes whom.</p>}
    </div>
    {groups.map(group => {
      const valid = group.entries.every(entry => entry.amount !== null && entry.amount !== '' && Number.isFinite(Number(entry.amount)) && ['Debit','Credit'].includes(entry.side));
      const net = group.entries.reduce((total, entry) => total + (entry.side === 'Debit' ? 1 : -1) * Math.round(Number(entry.amount) * 100), 0);
      const balanced = valid && net === 0;
      return <div className="ledger-books" key={group.key}>
        <div className="ledger-books-heading"><strong>{group.books}'s books</strong>
          <span style={{color:balanced ? theme.success : theme.danger}}>{!valid ? 'Unable to verify balance' : balanced ? 'Balanced' : `Out of balance by ${formatAmount(Math.abs(net) / 100, group.currency)}`}</span>
          <span className="ledger-currency">{group.currency || 'Currency not recorded'}</span>
        </div>
        <div className="posted-ledger-scroll" role="region" aria-label={`${group.books} ledger entries`} tabIndex={0}>
          <table><colgroup><col style={{width:'40%'}} /><col style={{width:110}} /><col style={{width:110}} /><col style={{width:'13%'}} /><col /></colgroup>
            <thead><tr><th scope="col">Account</th><th scope="col" className="ledger-amount">Debit</th><th scope="col" className="ledger-amount">Credit</th><th scope="col">Deal</th><th scope="col">Description</th></tr></thead>
            <tbody>{group.entries.map(entry => {
              const amount = formatAmount(entry.amount, entry.currency || currency);
              const debit = entry.side === 'Debit';
              const hint = accountHint?.(entry.account);
              const meaning = explanation(entry, group.books);
              const deal = entry.deal_name || deals.find(item => String(item.id) === String(entry.deal_id))?.deal_name;
              return <tr key={entry.id}>
                <td style={{paddingLeft:debit ? 8 : 24}}><div className="ledger-account">{entry.account}</div>{hint && <div className="ledger-account-hint">{hint}</div>}{meaning && <div className="ledger-account-hint">{meaning}</div>}</td>
                <td className="ledger-amount" style={{color:theme.success}}>{debit ? amount : ''}</td>
                <td className="ledger-amount" style={{color:theme.danger}}>{!debit ? amount : ''}</td>
                <td>{deal || '—'}</td><td className="ledger-description">{entry.description || '—'}</td>
              </tr>;
            })}</tbody>
          </table>
        </div>
      </div>;
    })}
  </section>;
}